/**
 * Training Calendar Heatmap - Widget 6  
 * 
 * GitHub-style contribution calendar showing daily practice intensity
 * Shows practice streaks, quality indicators, and session details
 */

import React, { useState, useMemo } from 'react';
import { CalendarData } from '@/types/statistics';

interface TrainingCalendarHeatmapProps {
  data: CalendarData[];
  loading: boolean;
}

type ViewMode = 'year' | 'month';

const TrainingCalendarHeatmap: React.FC<TrainingCalendarHeatmapProps> = ({ 
  data, 
  loading 
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('year');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  // Process calendar data
  const processedData = useMemo(() => {
    // Create a map for quick lookups
    const dataMap = new Map(data.map(item => [item.date, item]));
    
    // Generate calendar grid for the last year
    const today = new Date();
    const oneYearAgo = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
    
    const weeks: CalendarData[][] = [];
    let currentWeek: CalendarData[] = [];
    
    // Start from the beginning of the week containing one year ago
    const startDate = new Date(oneYearAgo);
    startDate.setDate(startDate.getDate() - startDate.getDay());
    
    for (let i = 0; i < 53; i++) { // 53 weeks to cover full year
      currentWeek = [];
      
      for (let j = 0; j < 7; j++) { // 7 days per week
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + (i * 7) + j);
        
        const dateStr = currentDate.toISOString().split('T')[0];
        const dayData = dataMap.get(dateStr);
        
        if (currentDate <= today) {
          currentWeek.push(dayData || {
            date: dateStr,
            intensity: 0,
            sessionsCount: 0,
            totalDuration: 0,
            accuracy: 0,
            qualityScore: 0,
            streak: false
          });
        }
      }
      
      if (currentWeek.length > 0) {
        weeks.push(currentWeek);
      }
    }
    
    return weeks;
  }, [data]);

  // Calculate statistics
  const calendarStats = useMemo(() => {
    const totalDays = data.length;
    const activeDays = data.filter(day => day.sessionsCount > 0).length;
    const currentStreak = calculateCurrentStreak(data);
    const longestStreak = calculateLongestStreak(data);
    const totalSessions = data.reduce((sum, day) => sum + day.sessionsCount, 0);
    const avgQuality = data.filter(day => day.sessionsCount > 0)
      .reduce((sum, day, _, arr) => sum + day.qualityScore / arr.length, 0);

    return {
      totalDays,
      activeDays,
      currentStreak,
      longestStreak,
      totalSessions,
      avgQuality,
      consistencyRate: totalDays > 0 ? Math.round((activeDays / totalDays) * 100) : 0
    };
  }, [data]);

  if (loading) {
    return <CalendarHeatmapSkeletonBlock />;
  }

  if (data.length === 0) {
    return <EmptyCalendarHeatmapBlock />;
  }

  const selectedDayData = selectedDate ? data.find(d => d.date === selectedDate) : null;
  const hoveredDayData = hoveredDate ? data.find(d => d.date === hoveredDate) : null;

  return (
    <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            📅 Practice Calendar
          </h2>
          <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px', margin: 0 }}>
            Your daily practice activity • Last 365 days
          </p>
        </div>

        {/* View Mode Toggle */}
        <div style={{ display: 'flex', background: '#334155', borderRadius: '6px', padding: '2px' }}>
          <button
            onClick={() => setViewMode('year')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              background: viewMode === 'year' ? '#475569' : 'transparent',
              color: '#e2e8f0'
            }}
          >
            📅 Year
          </button>
          <button
            onClick={() => setViewMode('month')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '500',
              border: 'none',
              cursor: 'pointer',
              background: viewMode === 'month' ? '#475569' : 'transparent',
              color: '#e2e8f0'
            }}
          >
            📊 Month
          </button>
        </div>
      </div>

      {/* Calendar Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px', marginBottom: '16px' }}>
        <StatCard
          icon="🔥"
          label="Current Streak"
          value={`${calendarStats.currentStreak} days`}
          color="#ea580c"
        />
        <StatCard
          icon="🏆"
          label="Longest Streak"
          value={`${calendarStats.longestStreak} days`}
          color="#ca8a04"
        />
        <StatCard
          icon="📈"
          label="Consistency"
          value={`${calendarStats.consistencyRate}%`}
          color="#16a34a"
        />
        <StatCard
          icon="📅"
          label="Active Days"
          value={`${calendarStats.activeDays}`}
          color="#3b82f6"
        />
        <StatCard
          icon="🎯"
          label="Total Sessions"
          value={`${calendarStats.totalSessions}`}
          color="#8b5cf6"
        />
        <StatCard
          icon="⭐"
          label="Avg Quality"
          value={`${calendarStats.avgQuality.toFixed(1)}/10`}
          color="#ec4899"
        />
      </div>

      {/* Calendar Heatmap */}
      <div style={{ background: '#334155', borderRadius: '6px', padding: '16px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>Practice Activity</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10px', color: '#94a3b8' }}>
            <span>Less</span>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[0, 1, 2, 3, 4].map(level => (
                <div
                  key={level}
                  style={{ width: '10px', height: '10px', borderRadius: '2px', ...getIntensityColorStyle(level) }}
                />
              ))}
            </div>
            <span>More</span>
          </div>
        </div>

        {/* Calendar Grid */}
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 'max-content' }}>
            {/* Month Labels */}
            <div style={{ display: 'flex', marginBottom: '6px' }}>
              <div style={{ width: '24px' }}></div> {/* Space for day labels */}
              {getMonthLabels().map((month, index) => (
                <div key={index} style={{ fontSize: '9px', color: '#94a3b8', textAlign: 'center', width: '40px' }}>
                  {month}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div style={{ display: 'flex' }}>
              {/* Day Labels */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '6px' }}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                  <div key={day} style={{ height: '10px', display: 'flex', alignItems: 'center' }}>
                    {index % 2 === 1 && (
                      <span style={{ fontSize: '9px', color: '#94a3b8', width: '18px' }}>{day}</span>
                    )}
                  </div>
                ))}
              </div>

              {/* Calendar Cells */}
              <div style={{ display: 'flex', gap: '2px' }}>
                {processedData.map((week, weekIndex) => (
                  <div key={weekIndex} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    {week.map((day, dayIndex) => (
                      <CalendarCell
                        key={`${weekIndex}-${dayIndex}`}
                        day={day}
                        isSelected={selectedDate === day.date}
                        onSelect={setSelectedDate}
                        onHover={setHoveredDate}
                        onLeave={() => setHoveredDate(null)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tooltip */}
        {hoveredDayData && (
          <div style={{ marginTop: '12px', padding: '8px', background: '#1e293b', borderRadius: '6px', border: '1px solid #475569' }}>
            <CalendarTooltip day={hoveredDayData} />
          </div>
        )}
      </div>

      {/* Selected Day Details */}
      {selectedDayData && (
        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #475569' }}>
          <SelectedDayDetails day={selectedDayData} onClose={() => setSelectedDate(null)} />
        </div>
      )}
    </div>
  );
};

// Individual Calendar Cell Component
const CalendarCell: React.FC<{
  day: CalendarData;
  isSelected: boolean;
  onSelect: (date: string) => void;
  onHover: (date: string) => void;
  onLeave: () => void;
}> = ({ day, isSelected, onSelect, onHover, onLeave }) => {

  const handleClick = () => {
    onSelect(day.date);
  };

  return (
    <div
      style={{
        width: '10px',
        height: '10px',
        borderRadius: '2px',
        cursor: 'pointer',
        border: isSelected ? '2px solid #3b82f6' : day.streak ? '1px solid #ea580c' : '1px solid transparent',
        ...getIntensityColorStyle(day.intensity)
      }}
      onClick={handleClick}
      onMouseEnter={() => onHover(day.date)}
      onMouseLeave={onLeave}
      title={`${formatDate(day.date)}: ${day.sessionsCount} sessions`}
    />
  );
};

// Calendar Tooltip Component
const CalendarTooltip: React.FC<{ day: CalendarData }> = ({ day }) => (
  <div>
    <div style={{ fontWeight: '500', color: '#e2e8f0', marginBottom: '6px', fontSize: '11px' }}>
      {formatDate(day.date)}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '10px' }}>
      <div>
        <span style={{ color: '#94a3b8' }}>Sessions:</span>
        <span style={{ marginLeft: '6px', fontWeight: '500', color: '#e2e8f0' }}>{day.sessionsCount}</span>
      </div>
      <div>
        <span style={{ color: '#94a3b8' }}>Duration:</span>
        <span style={{ marginLeft: '6px', fontWeight: '500', color: '#e2e8f0' }}>{formatDuration(day.totalDuration)}</span>
      </div>
      {day.sessionsCount > 0 && (
        <>
          <div>
            <span style={{ color: '#94a3b8' }}>Accuracy:</span>
            <span style={{ marginLeft: '6px', fontWeight: '500', color: '#e2e8f0' }}>{day.accuracy.toFixed(1)}%</span>
          </div>
          <div>
            <span style={{ color: '#94a3b8' }}>Quality:</span>
            <span style={{ marginLeft: '6px', fontWeight: '500', color: '#e2e8f0' }}>{day.qualityScore.toFixed(1)}/10</span>
          </div>
        </>
      )}
    </div>
    {day.streak && (
      <div style={{ marginTop: '6px', fontSize: '10px', color: '#ea580c', fontWeight: '500' }}>
        🔥 Part of current streak
      </div>
    )}
  </div>
);

// Selected Day Details Component
const SelectedDayDetails: React.FC<{ 
  day: CalendarData; 
  onClose: () => void;
}> = ({ day, onClose }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
      <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#e2e8f0' }}>
        Practice Details - {formatDate(day.date)}
      </h3>
      <button
        onClick={onClose}
        style={{ color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
      >
        ✕
      </button>
    </div>

    {day.sessionsCount > 0 ? (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <DetailItem
            icon="📅"
            label="Sessions Completed"
            value={day.sessionsCount.toString()}
          />
          <DetailItem
            icon="⏱️"
            label="Total Practice Time"
            value={formatDuration(day.totalDuration)}
          />
          <DetailItem
            icon="🎯"
            label="Accuracy"
            value={`${day.accuracy.toFixed(1)}%`}
            color={getAccuracyColor(day.accuracy)}
          />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <DetailItem
            icon="⭐"
            label="Session Quality"
            value={`${day.qualityScore.toFixed(1)}/10`}
            color={getQualityColor(day.qualityScore)}
          />
          <DetailItem
            icon="🔥"
            label="Intensity Level"
            value={getIntensityLabel(day.intensity)}
          />
          {day.streak && (
            <DetailItem
              icon="🏆"
              label="Streak Status"
              value="Part of current streak"
              color="#ea580c"
            />
          )}
        </div>
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>😴</div>
        <div style={{ fontWeight: '500' }}>No practice on this day</div>
        <div style={{ fontSize: '12px' }}>Consider maintaining consistency for better results!</div>
      </div>
    )}
  </div>
);

// Detail Item Component
const DetailItem: React.FC<{
  icon: string;
  label: string;
  value: string;
  color?: string;
}> = ({ icon, label, value, color = '#e2e8f0' }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{ fontSize: '16px' }}>{icon}</span>
    <div>
      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{label}</div>
      <div style={{ fontWeight: '500', color, fontSize: '12px' }}>{value}</div>
    </div>
  </div>
);

// Summary Stat Card Component
const StatCard: React.FC<{
  icon: string;
  label: string;
  value: string;
  color: string;
}> = ({ icon, label, value, color }) => (
  <div style={{ background: '#334155', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
    <div style={{ fontSize: '14px', marginBottom: '4px' }}>{icon}</div>
    <div style={{ fontSize: '9px', color: '#94a3b8', marginBottom: '2px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </div>
    <div style={{ fontSize: '12px', fontWeight: '700', color }}>
      {value}
    </div>
  </div>
);

// Helper Functions
function getIntensityColorStyle(intensity: number) {
  const colors = [
    { background: '#f3f4f6' },      // 0 - No activity
    { background: '#bbf7d0' },      // 1 - Light activity
    { background: '#86efac' },      // 2 - Moderate activity
    { background: '#22c55e' },      // 3 - High activity
    { background: '#15803d' }       // 4 - Very high activity
  ];
  return colors[Math.min(intensity, 4)] || colors[0];
}

function getIntensityLabel(intensity: number): string {
  const labels = ['None', 'Light', 'Moderate', 'High', 'Very High'];
  return labels[Math.min(intensity, 4)] || 'None';
}

function getAccuracyColor(accuracy: number): string {
  if (accuracy >= 95) return '#16a34a';
  if (accuracy >= 90) return '#3b82f6';
  if (accuracy >= 85) return '#ca8a04';
  return '#dc2626';
}

function getQualityColor(quality: number): string {
  if (quality >= 8) return '#16a34a';
  if (quality >= 6) return '#ca8a04';
  return '#dc2626';
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${minutes}m`;
}

function getMonthLabels(): string[] {
  const months = [];
  const today = new Date();
  
  for (let i = 12; i >= 0; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    months.push(date.toLocaleDateString('en-US', { month: 'short' }));
  }
  
  return months;
}

function calculateCurrentStreak(data: CalendarData[]): number {
  if (data.length === 0) return 0;
  
  // Sort by date descending (most recent first)
  const sortedData = [...data].sort((a, b) => b.date.localeCompare(a.date));
  
  let streak = 0;
  for (const day of sortedData) {
    if (day.sessionsCount > 0) {
      streak++;
    } else {
      break;
    }
  }
  
  return streak;
}

function calculateLongestStreak(data: CalendarData[]): number {
  if (data.length === 0) return 0;
  
  // Sort by date ascending
  const sortedData = [...data].sort((a, b) => a.date.localeCompare(b.date));
  
  let maxStreak = 0;
  let currentStreak = 0;
  
  for (const day of sortedData) {
    if (day.sessionsCount > 0) {
      currentStreak++;
      maxStreak = Math.max(maxStreak, currentStreak);
    } else {
      currentStreak = 0;
    }
  }
  
  return maxStreak;
}

// Loading Skeleton
const CalendarHeatmapSkeletonBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <div>
        <div style={{ height: '16px', background: '#475569', borderRadius: '4px', width: '180px', marginBottom: '8px' }}></div>
        <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '200px' }}></div>
      </div>
      <div style={{ height: '32px', background: '#475569', borderRadius: '6px', width: '80px' }}></div>
    </div>
    
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px', marginBottom: '16px' }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} style={{ background: '#334155', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
          <div style={{ height: '14px', background: '#475569', borderRadius: '4px', marginBottom: '4px', margin: '0 auto', width: '14px' }}></div>
          <div style={{ height: '9px', background: '#475569', borderRadius: '4px', width: '48px', marginBottom: '2px', margin: '0 auto' }}></div>
          <div style={{ height: '12px', background: '#475569', borderRadius: '4px', width: '36px', margin: '0 auto' }}></div>
        </div>
      ))}
    </div>
    
    <div style={{ background: '#334155', borderRadius: '6px', padding: '16px' }}>
      <div style={{ height: '14px', background: '#475569', borderRadius: '4px', width: '120px', marginBottom: '12px' }}></div>
      <div style={{ height: '120px', background: '#475569', borderRadius: '6px' }}></div>
    </div>
  </div>
);

// Empty State
const EmptyCalendarHeatmapBlock: React.FC = () => (
  <div style={{ background: '#1e293b', borderRadius: '8px', border: '1px solid #334155', padding: '16px', marginBottom: '16px' }}>
    <div style={{ textAlign: 'center', padding: '48px 0' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
      <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#ffffff', marginBottom: '8px' }}>
        No Calendar Data
      </h3>
      <p style={{ color: '#94a3b8', marginBottom: '16px', fontSize: '14px' }}>
        No practice calendar data available yet.
      </p>
      <div style={{ fontSize: '12px', color: '#64748b' }}>
        Start practicing regularly to see your activity calendar! 🎯
      </div>
    </div>
  </div>
);

export default TrainingCalendarHeatmap;